import type { PageMetadata } from '../lib/seo';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

interface SeoHeadProps {
    meta: PageMetadata;
}

/**
 * Applies the one piece of page metadata the BROWSER owns: the tab title.
 *
 * Everything else in `meta` — description, canonical, robots, OG/Twitter — is
 * emitted into the raw HTML by the SSG (`injectHeadTags` in lib/ssgHead.ts) and is
 * deliberately NOT re-emitted here. See hooks/useDocumentTitle.ts for the measured
 * reason: React's native document metadata ADDS tags rather than replacing the
 * SSG's, which produced two canonicals with different hrefs and two contradicting
 * robots values on every prerendered page.
 *
 * This renders no DOM. It is kept as a component (rather than callers using the
 * hook directly) so the ~11 call sites keep reading as "this page's SEO metadata is
 * X", and so there is a single place to change if the head strategy changes again.
 *
 * DO NOT re-add <Helmet>, and do not render <title>/<meta>/<link> from here. The
 * react-helmet-async implementation this replaced was inert under React 19 —
 * bundled, mounted, and producing nothing — and the native replacement duplicates
 * the SSG's tags rather than superseding them.
 */
/**
 * ⚠️⚠️ UI-122 — DESTRUCTURED IN THE PARAMETER LIST ON PURPOSE. DO NOT "TIDY" IT BACK.
 *
 * `{ meta: { title } }` binds ONLY `title`. The identifier `meta` never exists inside this
 * function, so `meta.ogImage` — which every gym profile really does pass — cannot be reached
 * by an incremental edit. Widening it is now a deliberate signature change, not a one-liner.
 *
 * WHY THAT MATTERS, and it is a COST consequence rather than a correctness one: the gym page
 * builds a social-image URL against the PAID photo proxy (`getGymOgImageUrl`, called on every
 * profile view) and hands it here, where it is dropped. Emitting it would mint a THIRTEENTH
 * paid cache key per gym — the existing twelve being four widths × three photos.
 *
 * ⚠️ AND THE REASON A COMMENT WAS NOT ENOUGH: `lib/headLayer.test.tsx` already carried a test
 * named "SeoHead renders NO DOM of its own", whose fixture even sets `ogImage` — and a mutant
 * emitting `og:image` left all 15 tests GREEN. React 19 HOISTS metadata into `<head>`, so the
 * tag leaves the container the assertion inspects. That test is now widened to assert the head
 * itself; this destructure and that assertion are a PAIR, and neither alone does both jobs.
 */
export function SeoHead({ meta: { title } }: SeoHeadProps) {
    useDocumentTitle(title);
    return null;
}
